import { parseIsoDate } from "./date.ts";
import { DomainError, type ActivePeriod, type Category, type IsoDate, type Operation } from "./model.ts";

export interface LifecycleEntity {
  readonly activePeriods: readonly ActivePeriod[];
}

export function canonicalActivePeriods(periods: readonly ActivePeriod[]): ActivePeriod[] {
  const sorted = periods.map((period): ActivePeriod => {
    parseIsoDate(period.from);
    if (period.toExclusive !== null) {
      parseIsoDate(period.toExclusive);
      if (period.toExclusive <= period.from) throw new DomainError("INVALID_ACTIVE_PERIOD", "active period end must follow its start");
    }
    return { from: period.from, toExclusive: period.toExclusive };
  }).sort((left, right) => left.from.localeCompare(right.from));
  for (let index = 1; index < sorted.length; index += 1) {
    const previous = sorted[index - 1]!;
    const current = sorted[index]!;
    if (previous.toExclusive === null || current.from < previous.toExclusive) {
      throw new DomainError("OVERLAPPING_ACTIVE_PERIODS", "active periods cannot overlap");
    }
  }
  return sorted;
}

export function isActiveOn(periods: readonly ActivePeriod[], date: IsoDate): boolean {
  parseIsoDate(date);
  return periods.some((period) => period.from <= date && (period.toExclusive === null || date < period.toExclusive));
}

/** Archive is inclusive of effectiveFrom and removes every later active period. */
export function archiveActivePeriods(periods: readonly ActivePeriod[], effectiveFrom: IsoDate): ActivePeriod[] {
  parseIsoDate(effectiveFrom);
  return canonicalActivePeriods(periods).flatMap((period): ActivePeriod[] => {
    if (period.from >= effectiveFrom) return [];
    if (period.toExclusive === null || effectiveFrom < period.toExclusive) return [{ from: period.from, toExclusive: effectiveFrom }];
    return [period];
  });
}

/** Restore creates a distinct active period and never fills the preceding gap. */
export function restoreActivePeriods(periods: readonly ActivePeriod[], effectiveFrom: IsoDate): ActivePeriod[] {
  parseIsoDate(effectiveFrom);
  const canonical = canonicalActivePeriods(periods);
  if (isActiveOn(canonical, effectiveFrom)) throw new DomainError("ALREADY_ACTIVE", `item is already active on ${effectiveFrom}`);
  const nextPeriod = canonical.find((period) => period.from > effectiveFrom);
  return canonicalActivePeriods([...canonical, { from: effectiveFrom, toExclusive: nextPeriod?.from ?? null }]);
}

export function archiveStarting<T extends LifecycleEntity>(entity: T, effectiveFrom: IsoDate): T {
  return { ...entity, activePeriods: archiveActivePeriods(entity.activePeriods, effectiveFrom) };
}

export function restoreStarting<T extends LifecycleEntity>(entity: T, effectiveFrom: IsoDate): T {
  return { ...entity, activePeriods: restoreActivePeriods(entity.activePeriods, effectiveFrom) };
}

export function selectCategoryArchiveOperationIds(category: Category, operations: readonly Operation[], effectiveFrom: IsoDate): string[] {
  parseIsoDate(effectiveFrom);
  if (!isActiveOn(category.activePeriods, effectiveFrom)) return [];
  const order = new Map(category.operationOrder.map((operationId, index) => [operationId, index]));
  return operations.map((operation, inputIndex) => ({ operation, inputIndex }))
    .filter(({ operation }) => operation.categoryId === category.id && isActiveOn(operation.activePeriods, effectiveFrom))
    .sort((left, right) => (order.get(left.operation.id) ?? Number.MAX_SAFE_INTEGER) -
      (order.get(right.operation.id) ?? Number.MAX_SAFE_INTEGER) || left.inputIndex - right.inputIndex)
    .map(({ operation }) => operation.id);
}

/** Cascade-archived operations start selected; explicit deselection preserves their gaps. */
export function selectCategoryRestoreOperationIds(cascadeOperationIds: readonly string[], deselectedOperationIds: readonly string[] = []): string[] {
  const deselected = new Set(deselectedOperationIds);
  return [...new Set(cascadeOperationIds)].filter((operationId) => !deselected.has(operationId));
}
